import {
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Paper,
  TablePagination,
  TableSortLabel,
  Checkbox,
  Typography,
  Box,
  CircularProgress,
  IconButton,
  Tooltip,
} from '@mui/material';
import { AdminTableColumn, AdminTableAction } from '../../../types/admin';

interface AdminTableProps<T> {
  columns: AdminTableColumn<T>[];
  data: T[];
  isLoading?: boolean;
  error?: string | null;
  pagination?: {
    page: number;
    limit: number;
    total: number;
    onPageChange: (page: number) => void;
    onLimitChange: (limit: number) => void;
  };
  sorting?: {
    sortBy: keyof T | null;
    sortOrder: 'asc' | 'desc';
    onSort: (column: keyof T) => void;
  };
  selection?: {
    selectedIds: string[];
    onSelect: (ids: string[]) => void;
  };
  actions?: AdminTableAction<T>[];
  emptyState?: React.ReactNode;
}

/**
 * AdminTable - Reusable data table component for all admin sections
 *
 * Features:
 * - Sortable columns
 * - Pagination
 * - Row selection
 * - Custom cell rendering
 * - Row actions
 * - Loading and error states
 *
 * TODO: Implement sorting functionality
 * TODO: Implement selection functionality
 * TODO: Implement row actions
 * TODO: Add bulk actions toolbar
 */
export default function AdminTable<T extends { id: string }>({
  columns,
  data,
  isLoading,
  error,
  pagination,
  sorting,
  selection,
  actions,
  emptyState,
}: AdminTableProps<T>) {
  // Loading state
  if (isLoading) {
    return (
      <Box sx={{ display: 'flex', justifyContent: 'center', p: 4 }}>
        <CircularProgress />
      </Box>
    );
  }

  // Error state
  if (error) {
    return (
      <Box sx={{ p: 4, textAlign: 'center' }}>
        <Typography color="error">{error}</Typography>
      </Box>
    );
  }

  // Empty state
  if (data.length === 0) {
    return (
      <Box sx={{ p: 4, textAlign: 'center' }}>
        {emptyState || <Typography color="text.secondary">No data available</Typography>}
      </Box>
    );
  }

  return (
    <Paper>
      <TableContainer>
        <Table>
          <TableHead>
            <TableRow>
              {selection && (
                <TableCell padding="checkbox">
                  <Checkbox
                    // TODO: Implement select all functionality
                    disabled
                  />
                </TableCell>
              )}
              {columns.map((column) => (
                <TableCell
                  key={String(column.key)}
                  style={{ width: column.width }}
                >
                  {column.sortable && sorting ? (
                    <TableSortLabel
                      active={sorting.sortBy === column.key}
                      direction={sorting.sortBy === column.key ? sorting.sortOrder : 'asc'}
                      onClick={() => sorting.onSort(column.key)}
                    >
                      {column.label}
                    </TableSortLabel>
                  ) : (
                    column.label
                  )}
                </TableCell>
              ))}
              {actions && <TableCell>Actions</TableCell>}
            </TableRow>
          </TableHead>
          <TableBody>
            {data.map((row) => (
              <TableRow key={row.id} hover>
                {selection && (
                  <TableCell padding="checkbox">
                    <Checkbox
                      // TODO: Implement row selection
                      disabled
                    />
                  </TableCell>
                )}
                {columns.map((column) => (
                  <TableCell key={String(column.key)}>
                    {column.render ? column.render(row) : String(row[column.key])}
                  </TableCell>
                ))}
                {actions && (
                  <TableCell>
                    <Box sx={{ display: 'flex', gap: 0.5 }}>
                      {actions.map((action, index) => {
                        const isDisabled = action.disabled ? action.disabled(row) : false;
                        return (
                          <Tooltip key={index} title={action.label}>
                            <span>
                              <IconButton
                                size="small"
                                onClick={() => action.onClick(row)}
                                disabled={isDisabled}
                                color={action.color || 'default'}
                              >
                                {action.icon}
                              </IconButton>
                            </span>
                          </Tooltip>
                        );
                      })}
                    </Box>
                  </TableCell>
                )}
              </TableRow>
            ))}
          </TableBody>
        </Table>
      </TableContainer>

      {pagination && (
        <TablePagination
          component="div"
          count={pagination.total}
          page={pagination.page - 1}
          onPageChange={(_event, newPage) => pagination.onPageChange(newPage + 1)}
          rowsPerPage={pagination.limit}
          onRowsPerPageChange={(event) =>
            pagination.onLimitChange(parseInt(event.target.value, 10))
          }
          rowsPerPageOptions={[25, 50, 100]}
        />
      )}
    </Paper>
  );
}
